import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import Icon from '../../../../ui/Icon';
import { shadeColor, toRgba } from '../../../../ui/theme/colors';

const VisionCard = styled(Flex)`
  background: ${(props) => {
    const color = props.$brandColor || theme.colors.PRIMARY;
    return `linear-gradient(135deg, ${shadeColor(color, 0.97)} 0%, ${shadeColor(color, 0.94)} 100%)`;
  }};
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 2px 12px ${toRgba(theme.colors.NEU_999, 0.04)};
  margin-bottom: 40px;
  width: 100%;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 20px ${toRgba(theme.colors.NEU_999, 0.08)};
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ContentWrapper = styled(Flex)`
  width: 100%;
  gap: 32px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const TextSection = styled(Flex)`
  flex: 1;
  min-width: 0;
`;

const MetricsSection = styled(Flex)`
  gap: 24px;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: space-around;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const Label = styled(Typo.SmallText)`
  color: ${theme.colors.NEU_600};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  margin-bottom: 12px;
  font-size: 11px;
`;

const VisionTitle = styled(Typo.PageHeader)`
  color: ${theme.colors.NEU_PRIMARY};
  font-weight: 700;
  margin-bottom: 12px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const VisionDescription = styled(Typo.MediumText)`
  color: ${theme.colors.NEU_600};
  line-height: 1.6;
  margin-bottom: 16px;
`;

const Deadline = styled(Flex)`
  gap: 8px;
  align-items: center;
  margin-top: 8px;
`;

const DeadlineText = styled(Typo.Info)`
  color: ${theme.colors.NEU_600};
  font-weight: 500;
`;

const MetricCard = styled(Flex)`
  background: ${theme.colors.NEU_0};
  padding: 24px 20px;
  border-radius: 16px;
  min-width: 140px;
  box-shadow: 0 1px 6px ${toRgba(theme.colors.NEU_999, 0.03)};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 10px ${toRgba(theme.colors.NEU_999, 0.06)};
  }

  @media (max-width: 900px) {
    min-width: 120px;
  }

  @media (max-width: 600px) {
    width: 100%;
    min-width: auto;
  }
`;

const MetricValue = styled(Typo.SectionHeader)`
  color: ${(props) => props.$color || theme.colors.NEU_PRIMARY};
  font-weight: 700;
  margin-bottom: 4px;
`;

const MetricLabel = styled(Typo.SmallText)`
  color: ${theme.colors.NEU_600};
  font-size: 12px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.NEU_100};
  border-radius: 4px;
  overflow: hidden;
  margin-top: 16px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: ${(props) => props.$color || theme.colors.PRIMARY};
  border-radius: 4px;
  transition: width 0.6s ease;
  width: ${(props) => props.$percentage || 0}%;
`;

const IconWrapper = styled(Flex)`
  color: ${(props) => props.$color || theme.colors.PRIMARY};
  margin-right: 4px;
`;

const GoalsSummarySection = styled(Flex)`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${theme.colors.NEU_100};
  width: 100%;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const GoalSummaryItem = styled(Flex)`
  flex: 1;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  background: ${theme.colors.NEU_0};
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
    transform: translateY(-2px);
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const GoalSummaryIcon = styled(Flex)`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => {
    const color = props.$color || theme.colors.PRIMARY;
    return toRgba(color, 0.06);
  }};
  color: ${(props) => props.$color || theme.colors.PRIMARY};
  flex-shrink: 0;
`;

const GoalSummaryContent = styled(Flex)`
  flex: 1;
  min-width: 0;
`;

const GoalSummaryTitle = styled(Typo.SmallText)`
  color: ${theme.colors.NEU_600};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 2px;
`;

const GoalSummaryValue = styled(Typo.TextTitle)`
  color: ${theme.colors.NEU_PRIMARY};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VisionGoalCard = ({ visionGoal, goals = [], brandColor, onGoalClick }) => {
  if (!visionGoal) return null;

  const { title, description, deadline, progress = 0, supportingGoalsCount = 3, supportingGoalsCompleted = 0 } = visionGoal;

  const handleGoalClick = (goalId) => {
    if (onGoalClick) {
      onGoalClick(goalId);
    }
  };

  const formatDeadline = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    const end = new Date(dateString);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const daysRemaining = getDaysRemaining(deadline);
  const progressPercentage = Math.round(progress * 100);

  return (
    <VisionCard direction='column' align='flex-start' $brandColor={brandColor}>
      <ContentWrapper align='flex-start'>
        <TextSection direction='column' align='flex-start'>
          <Label>Your Vision</Label>
          <VisionTitle>{title}</VisionTitle>
          <VisionDescription>{description}</VisionDescription>

          {deadline && (
            <Deadline>
              <IconWrapper $color={brandColor}>
                <Icon.Calendar size='x-small' />
              </IconWrapper>
              <DeadlineText>
                Target: {formatDeadline(deadline)}
                {daysRemaining !== null && ` (${daysRemaining} days remaining)`}
              </DeadlineText>
            </Deadline>
          )}

          <ProgressBarContainer>
            <ProgressBarFill $percentage={progressPercentage} $color={brandColor} />
          </ProgressBarContainer>
        </TextSection>

        <MetricsSection>
          <MetricCard direction='column' align='center'>
            <MetricValue $color={brandColor}>{progressPercentage}%</MetricValue>
            <MetricLabel>Complete</MetricLabel>
          </MetricCard>

          <MetricCard direction='column' align='center'>
            <MetricValue $color={brandColor}>
              {supportingGoalsCompleted}/{supportingGoalsCount}
            </MetricValue>
            <MetricLabel>Goals</MetricLabel>
          </MetricCard>

          {daysRemaining !== null && (
            <MetricCard direction='column' align='center'>
              <MetricValue $color={brandColor}>{daysRemaining}</MetricValue>
              <MetricLabel>Days Left</MetricLabel>
            </MetricCard>
          )}
        </MetricsSection>
      </ContentWrapper>

      {goals && goals.length > 0 && (
        <GoalsSummarySection>
          {goals.map((goal) => (
            <GoalSummaryItem key={goal.id} onClick={() => handleGoalClick(goal.id)}>
              <GoalSummaryIcon align='center' justify='center' $color={brandColor}>
                {goal.type === 'revenue' && <Icon.DollarSign size='small' />}
                {goal.type === 'creation' && <Icon.Course size='small' />}
                {goal.type === 'distribution' && <Icon.Users size='small' />}
              </GoalSummaryIcon>
              <GoalSummaryContent direction='column' align='flex-start'>
                <GoalSummaryTitle>{goal.title}</GoalSummaryTitle>
                <GoalSummaryValue>
                  {goal.current} / {goal.target} {goal.currency || ''}
                </GoalSummaryValue>
              </GoalSummaryContent>
            </GoalSummaryItem>
          ))}
        </GoalsSummarySection>
      )}
    </VisionCard>
  );
};

export default VisionGoalCard;
